/**
 * The value half of a scope rule: a searchable, server-paged
 * multi-select over the LIVE project file.
 *
 * IT HAS NO FREE-TEXT ESCAPE, AND THAT IS THE DESIGN
 * --------------------------------------------------
 * `DimensionFormModal.ColumnPicker` has one, and the difference is
 * real: that picker chooses a COLUMN out of
 * `projects.data_source_schema`, which is what the last reload
 * RECORDED, while the backend validates against the live file — so a
 * real column could otherwise be unpickable. Here the values already
 * come from the live file. A typed value can therefore only be
 *
 *   (a) a value that does not exist, which produces a `no_mapping`
 *       build: an EMPTY dashboard with no error anywhere, or
 *   (b) a type mismatch, which the predicate refuses at build time.
 *
 * `total`, `null_count` and an honest "no results for «x»" are the
 * correct affordances. A text box is not.
 *
 * THE VALUE'S JSON TYPE IS LOAD-BEARING
 * -------------------------------------
 * `DimensionValueOut.value` is `unknown` and stays `unknown` all the way
 * into `rules_json`. `dimensions-api.ts` says why on the wire side (an
 * INTEGER column answers with numbers); the runbook measured the cost on
 * the data side, in DuckDB 1.5.3: against a VARCHAR column a rule of
 * `[1]` makes DuckDB cast the COLUMN, so `'1'`, `'01'` and `'1.0'` all
 * match — *"una edición de JSON indistinguible a ojo entrega tres
 * vendedores distintos"*. So selection identity is `JSON.stringify`,
 * never `String()`: `1` and `"1"` are two different values here and the
 * picker must not merge them.
 *
 * NULL IS NOT SELECTABLE
 * ----------------------
 * `null_count` is reported and NULL is not offered, because
 * `precheck_predicate` refuses a rule containing one (`null_in_values`)
 * and the runbook states the consequence plainly: rows whose value is
 * NULL belong to NO restricted scope. The sentence is on screen instead
 * of a checkbox that cannot be saved.
 */

import { Button, Input } from "@omnitok/ui";
import { Callout, EmptyState } from "@pervasivemind/admin-ui";
import { Spinner } from "@pervasivemind/bi-components";
import { Check, Plus, SearchX, X } from "lucide-react";
import { useEffect, useState } from "react";

import { useT } from "@/i18n";
import {
  dimensionsApi,
  type DimensionValuesOut,
} from "@/lib/dimensions-api";

/** Selection identity. `JSON.stringify` and not `String`: it is what
 *  keeps `1` and `"1"` apart, which is the whole point. */
export function valueKey(value: unknown): string {
  const encoded = JSON.stringify(value);
  // `JSON.stringify(undefined)` is `undefined`, not a string. Mapping
  // that to `"null"` would make the two share a selection key — the
  // same silent merge this function exists to prevent, one level up.
  // Nothing on the wire is `undefined`, so this is a tripwire, not a
  // path anybody should reach.
  // The sentinel is readable ASCII on purpose: a literal NUL byte
  // would make `grep` report this file as binary, which is exactly
  // the trap `builder-api.ts` already carries. No `JSON.stringify`
  // output can equal it (a string value encodes WITH its quotes).
  return encoded === undefined ? "<undefined>" : encoded;
}

export function hasValue(selected: readonly unknown[], value: unknown): boolean {
  const key = valueKey(value);
  return selected.some((v) => valueKey(v) === key);
}

/** Add or remove, preserving the ORIGINAL value (not a re-parse of the
 *  key), so the type survives the round trip. */
export function toggleValue(
  selected: readonly unknown[],
  value: unknown,
): unknown[] {
  const key = valueKey(value);
  return hasValue(selected, value)
    ? selected.filter((v) => valueKey(v) !== key)
    : [...selected, value];
}

const PAGE = 50;

export function DimensionValuePicker({
  projectSlug,
  dimensionId,
  selected,
  onChange,
}: {
  projectSlug: string;
  dimensionId: string;
  selected: readonly unknown[];
  onChange: (next: unknown[]) => void;
}): JSX.Element {
  const { t } = useT();
  const [search, setSearch] = useState("");
  const [offset, setOffset] = useState(0);
  const [data, setData] = useState<DimensionValuesOut | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Only the NEWEST request may write: every keystroke and every
    // reference-project change fires one, and they resolve in any order.
    let alive = true;
    setLoading(true);
    setError(null);
    void (async () => {
      try {
        const out = await dimensionsApi.values(projectSlug, dimensionId, {
          search: search === "" ? undefined : search,
          offset,
          limit: PAGE,
        });
        if (!alive) return;
        setData(out);
      } catch (err) {
        if (!alive) return;
        // Stated, never swallowed: without the values there is nothing
        // honest to author a rule from.
        setData(null);
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [projectSlug, dimensionId, search, offset]);

  return (
    <div className="space-y-2">
      {/* What is already chosen, including values not on this page. A
          chip that vanished when the search moved would look like the
          rule had lost it. */}
      <div className="flex flex-wrap items-center gap-1.5">
        {selected.length === 0 ? (
          <span className="text-[13px] text-neutral-500">
            {t("access.scopes.values.noneSelected")}
          </span>
        ) : (
          selected.map((value) => (
            <button
              key={valueKey(value)}
              type="button"
              onClick={() => onChange(toggleValue(selected, value))}
              title={t("access.scopes.values.remove")}
              className="inline-flex items-center gap-1 rounded-full border border-primary-light bg-primary-soft px-2.5 py-0.5 font-mono text-xs font-semibold text-primary-ink"
            >
              <Check size={12} strokeWidth={3} aria-hidden="true" />
              {String(value)}
              <X size={12} strokeWidth={3} aria-hidden="true" />
            </button>
          ))
        )}
      </div>

      <Input
        value={search}
        aria-label={t("access.scopes.values.searchLabel")}
        placeholder={t("access.scopes.values.searchPlaceholder")}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
          setSearch(e.target.value);
          setOffset(0);
        }}
      />

      {error !== null && (
        <Callout tone="danger" title={t("access.scopes.values.errorTitle")}>
          <span className="whitespace-pre-wrap">{error}</span>
        </Callout>
      )}

      {loading && (
        <span className="inline-flex items-center gap-1.5 text-[13px] text-neutral-500">
          <Spinner size={12} ariaLabel={t("common.loading")} />
          {t("common.loading")}
        </span>
      )}

      {data !== null && !data.scopable && (
        <Callout
          tone="warning"
          title={t("access.scopes.values.notScopableTitle")}
        >
          {t("access.scopes.values.notScopable", { type: data.column_type })}
        </Callout>
      )}

      {data !== null && (
        <>
          <p className="text-[13px] text-neutral-600">
            {t("access.scopes.values.summary", {
              shown: data.values.length,
              total: data.total,
              column: data.column,
              type: data.column_type,
            })}
          </p>
          <p className="text-[13px] text-neutral-600">
            {t("access.scopes.values.nulls", { n: data.null_count })}
          </p>

          {data.values.length === 0 ? (
            <EmptyState
              icon={<SearchX size={20} />}
              title={
                search === ""
                  ? t("access.scopes.values.emptyTitle")
                  : t("access.scopes.values.noMatchTitle", { search })
              }
              description={t("access.scopes.values.noMatchDesc")}
            />
          ) : (
            <ul className="flex flex-wrap gap-1.5">
              {data.values.map((entry) => {
                const on = hasValue(selected, entry.value);
                return (
                  <li key={valueKey(entry.value)}>
                    <button
                      type="button"
                      aria-pressed={on}
                      onClick={() => onChange(toggleValue(selected, entry.value))}
                      title={t("access.scopes.values.rowCount", {
                        n: entry.row_count,
                      })}
                      /* Four channels, not one: glyph, border, weight
                         and `aria-pressed`. The audit that produced this
                         grammar on the page being replaced found on/off
                         differing only by a pale tint. */
                      className={
                        on
                          ? "inline-flex items-center gap-1 rounded-full border border-primary-light bg-primary-soft px-2.5 py-0.5 font-mono text-xs font-semibold text-primary-ink"
                          : "inline-flex items-center gap-1 rounded-full border border-neutral-300 bg-white px-2.5 py-0.5 font-mono text-xs font-medium text-neutral-600 hover:bg-neutral-50"
                      }
                    >
                      {on ? (
                        <Check size={12} strokeWidth={3} aria-hidden="true" />
                      ) : (
                        <Plus size={12} strokeWidth={3} aria-hidden="true" />
                      )}
                      {String(entry.value)}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}

          {/* `total` is the REAL distinct count after `search` — never
              capped, never the page length — so paging says which is
              which instead of implying the list is all of it. */}
          {data.total > PAGE && (
            <div className="flex items-center gap-2">
              <Button
                ghost
                size="xs"
                disabled={offset === 0}
                onClick={() => setOffset(Math.max(0, offset - PAGE))}
              >
                {t("access.common.prev")}
              </Button>
              <span className="text-[13px] text-neutral-600">
                {t("access.scopes.values.range", {
                  from: data.total === 0 ? 0 : offset + 1,
                  to: offset + data.values.length,
                  total: data.total,
                })}
              </span>
              <Button
                ghost
                size="xs"
                disabled={offset + PAGE >= data.total}
                onClick={() => setOffset(offset + PAGE)}
              >
                {t("access.common.next")}
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
